import React, { useEffect, useState } from "react";
import { clsx } from "clsx";
import Cookie from "js-cookie";
import { Lock, Stars } from "@bloomberg/media-ui-iconography";
import { ChevronDownThin, ChevronUpThin } from "@bloomberg/media-ui-icons";
import { CallToActionBanner } from "@bloomberg/media-ui-subscriptions";
import { useFortress } from "@foundation/fortress";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { useStatsigClient } from "@foundation/statsig";

import type { Story } from "~types/types";
import { getFortressState } from "~utils/getFortressState";
import { TranslatedMessage, useTranslateMessage } from "~utils/i18n/translate";
import { Tracker } from "~utils/tracker";

import styles from "./SummaryOnlyTakeaways.module.css";

interface SummaryOnlyTakeawaysProps {
    story: Story;
}

const tracker = new Tracker({
    action: "click",
    event: "Takeaways",
});

const AI_SUMMARY_CONTENT_ID = "ai-summary-content";
const SURVEY_URL =
    "https://bmedia.iad1.qualtrics.com/jfe/form/SV_8GGioK8vvTcx1Pg?source=takeaways";

const SummaryOnlyTakeaways = ({ story }: SummaryOnlyTakeawaysProps) => {
    const [isOpen, setIsOpen] = useState(true);
    const fortressContext = useFortress();
    const { isBlocked: isFortressBlocked, isLoading: isFortressLoading } =
        getFortressState(fortressContext);
    const { checkGate } = useStatsigClient();
    const { session: regUISession } = useRegUIClient();
    const bconnectId = regUISession?.userInfo?.userId ?? "";
    const isBannerFlagEnabled = checkGate("ai_takeaways_survey_link");
    const translateMessage = useTranslateMessage();

    const handleOnExpand = () => {
        setIsOpen((prevState) => {
            const newState = !prevState;

            Cookie.set("_takeaways-open", newState.toString());

            if (newState === false) {
                tracker.trackEvent({
                    category: "takeaways-collapse",
                    label: "collapse-takeaways",
                });
            }

            return newState;
        });
    };

    useEffect(() => {
        if (isFortressBlocked) {
            setIsOpen(false);
        }

        const cookie = Cookie.get("_takeaways-open");

        if (cookie) {
            setIsOpen(cookie === "true");
        }
    }, [isFortressBlocked]);

    if (!story?.aiSummary?.text?.length) {
        return null;
    }

    return (
        <>
            <div className={styles.takeaways}>
                <h3 className={styles.title}>
                    <span className={styles.identity}>
                        <Stars className={styles.starsIcon} />

                        <span>
                            <TranslatedMessage
                                id="aiTakeaways"
                                defaultMessage={
                                    "<strong>Takeaways </strong> by Bloomberg AI"
                                }
                                values={{
                                    strong: (chunks: string) => (
                                        <strong>{chunks}</strong>
                                    ),
                                }}
                            />
                        </span>
                    </span>

                    {!isFortressLoading && isFortressBlocked && (
                        <a
                            className={styles.titleLink}
                            href="/subscriptions"
                            target="_blank"
                        >
                            <Lock className={styles.lockIcon} />
                            <TranslatedMessage
                                id="subscribe"
                                defaultMessage="Subscribe"
                            />
                        </a>
                    )}

                    {!isFortressLoading && !isFortressBlocked && (
                        <button
                            aria-controls={AI_SUMMARY_CONTENT_ID}
                            aria-expanded={isOpen}
                            className={styles.titleButton}
                            onClick={handleOnExpand}
                            type="button"
                        >
                            <span className={styles.titleButtonText}>
                                {isOpen
                                    ? translateMessage({
                                          id: "hide",
                                          defaultMessage: "Hide",
                                      })
                                    : translateMessage({
                                          id: "show",
                                          defaultMessage: "Show",
                                      })}
                            </span>

                            <span className={styles.arrowIcon}>
                                {isOpen ? (
                                    <ChevronUpThin />
                                ) : (
                                    <ChevronDownThin />
                                )}
                            </span>
                        </button>
                    )}
                </h3>

                {!isFortressLoading && !isFortressBlocked && (
                    <div
                        className={clsx(styles.content, {
                            [styles.isOpen]: isOpen,
                        })}
                        id={AI_SUMMARY_CONTENT_ID}
                    >
                        <div
                            className={styles.contentInner}
                            data-testid="summary-only-card"
                        >
                            <ul className={styles.summaryList}>
                                {story.aiSummary.text.map((text, index) => (
                                    <li key={`summary-item-${index}`}>
                                        {text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {!isFortressLoading &&
                !isFortressBlocked &&
                isBannerFlagEnabled && (
                    <div className={styles.surveyBannerWrapper}>
                        {/* @ts-expect-error The prop interface of this component includes an unused content prop, which should be removed in @bloomberg/media-ui-subscriptions */}
                        <CallToActionBanner
                            bannerContent={
                                <TranslatedMessage
                                    id="aiFeedbackPrompt"
                                    defaultMessage={
                                        "Tell us how AI is shaping your news experience. <u>Share your feedback</u>"
                                    }
                                    values={{
                                        u: (chunks: string) => <u>{chunks}</u>,
                                    }}
                                />
                            }
                            href={`${SURVEY_URL}&bconnectId=${bconnectId}`}
                            target="_blank"
                        />
                    </div>
                )}
        </>
    );
};

export default SummaryOnlyTakeaways;
